import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { environment } from "environment/environment";
import useStockApiWebsocket from "service/websocket/StockApiWebsocket";
import { useAuth } from "../../common/AuthContext";
import { getBrokerAccounts, getBrokerId } from "../../../service/api/BrokerAccountApi";
import {
  createExecutionRisk,
  deleteExecutionRisk,
  getAllExecutionRisk,
  getExecutionRiskById,
  getExecutionRiskId,
  updateExecutionRisk,
} from "../../../service/api/ExecutionRiskApi";
import "../AlgoTrading.css";
import "./PodSectorStrategy.css";

const toNumber = (value) => {
  const parsed = Number.parseFloat(String(value ?? "").replace(/,/g, "").replace("%", ""));
  return Number.isFinite(parsed) ? parsed : null;
};
const SYMBOL_SERIES_SUFFIXES = new Set(["EQ", "BE", "BZ", "BL", "SM", "ST"]);
const normalizeSymbol = (value) => {
  const exchangeTrimmed = String(value ?? "").trim().toUpperCase().split(":").pop() || "";
  const parts = exchangeTrimmed.split("-");
  return parts.length > 1 && SYMBOL_SERIES_SUFFIXES.has(parts[parts.length - 1])
    ? parts.slice(0, -1).join("-")
    : exchangeTrimmed;
};
const getRows = (payload) => [payload?.data, payload?.response?.data, payload?.data?.data, payload].find(Array.isArray) || [];
const getSectorName = (sector) =>
  String(sector?.index ?? sector?.sectorName ?? sector?.name ?? "")
    .trim()
    .toUpperCase()
    .replace(/^NIFTY\s+/, "");
const formatNumber = (value) => value == null ? "—" : Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const getSupportResistanceDifference = (support, resistance) =>
  support == null || resistance == null ? null : resistance - support;
const getPreviousCloseOpenDifference = (previousClose, open) =>
  previousClose == null || open == null ? null : open - previousClose;
const getResistanceSupportPercentage = (support, resistance) =>
  support == null || resistance == null || resistance === 0
    ? null
    : ((resistance - support) / resistance) * 100;
const formatSector = (sector) =>
  `${sector.name} (${sector.percent >= 0 ? "+" : ""}${formatNumber(sector.percent)}%)`;
const ordinal = (rank) => `${rank}${rank === 1 ? "st" : rank === 2 ? "nd" : "rd"}`;
const isValidTime24 = (value) =>
  /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(String(value ?? ""));
const isBrokerConnected = (status = "") =>
  ["SUCCESS", "CONNECTED", "ACTIVE", "VERIFIED"].includes(
    status.toUpperCase().replace(/[\s_-]/g, "")
  );
const getLtpCellClass = (stock) => {
  const hasLevels = stock.ltp != null && stock.support != null && stock.resistance != null;

  if (hasLevels && stock.ltp >= stock.support && stock.ltp >= stock.resistance) {
    return "pod-ltp-cell-positive";
  }
  if (hasLevels && stock.ltp <= stock.support && stock.ltp <= stock.resistance) {
    return "pod-ltp-cell-negative";
  }

  return "";
};
const STRATEGY_OPTIONS = [
  { value: "SECTOR", label: "SECTOR" },
  { value: "WSECTOR", label: "WSECTOR" },
  { value: "LTPGSR", label: "LTPGSR" },
  { value: "LTPLSR", label: "LTPLSR" },
  { value: "OGSR", label: "OGSR" },
  { value: "OLSR", label: "OLSR" },
  { value: "LTPG500", label: "LTPG500" },
];
const CONDITION_DESCRIPTIONS = {
  SECTOR: "Stock belongs to a selected highest or lowest sector",
  WSECTOR: "Stock does not have a matched sector",
  LTPGSR: "LTP ≥ Support and Resistance",
  LTPLSR: "LTP ≤ Support and Resistance",
  OGSR: "Open ≥ Support and Resistance",
  OLSR: "Open ≤ Support and Resistance",
  LTPG500: "LTP ≥ ₹500",
};
const DEFAULT_EXECUTION_RISK_SETTINGS = {
  tradingMode: "PAPER",
  productType: "INTRADAY",
  orderType: "MARKET",
  quantity: "1",
  startTime: "09:20",
  endTime: "15:15",
  squareOffTime: "15:20",
  stopLoss: "",
  target: "",
  maximumDailyLoss: "",
  maximumTradesPerDay: "5",
  allowBuy: true,
  allowSell: true,
  oneTradePerSymbolPerDay: true,
};

export const buildExecutionRiskPayload = (userId, settings) => ({
  userId: String(userId),
  tradingMode: settings.tradingMode,
  productType: settings.productType,
  orderType: settings.orderType,
  quantity: toNumber(settings.quantity),
  startTime: settings.startTime,
  endTime: settings.endTime,
  squareOffTime: settings.squareOffTime,
  stopLoss: toNumber(settings.stopLoss) ?? 0,
  target: toNumber(settings.target) ?? 0,
  maximumDailyLoss: toNumber(settings.maximumDailyLoss) ?? 0,
  maximumTradesPerDay: toNumber(settings.maximumTradesPerDay),
  allowBuy: Boolean(settings.allowBuy),
  allowSell: Boolean(settings.allowSell),
  oneTradePerSymbolPerDay: Boolean(settings.oneTradePerSymbolPerDay),
});

const executionRiskToSettings = (executionRisk) => ({
  ...DEFAULT_EXECUTION_RISK_SETTINGS,
  tradingMode: executionRisk?.tradingMode ?? DEFAULT_EXECUTION_RISK_SETTINGS.tradingMode,
  productType: executionRisk?.productType ?? DEFAULT_EXECUTION_RISK_SETTINGS.productType,
  orderType: executionRisk?.orderType ?? DEFAULT_EXECUTION_RISK_SETTINGS.orderType,
  quantity: executionRisk?.quantity ?? DEFAULT_EXECUTION_RISK_SETTINGS.quantity,
  startTime: String(executionRisk?.startTime ?? DEFAULT_EXECUTION_RISK_SETTINGS.startTime).slice(0, 5),
  endTime: String(executionRisk?.endTime ?? DEFAULT_EXECUTION_RISK_SETTINGS.endTime).slice(0, 5),
  squareOffTime: String(executionRisk?.squareOffTime ?? DEFAULT_EXECUTION_RISK_SETTINGS.squareOffTime).slice(0, 5),
  stopLoss: executionRisk?.stopLoss ?? DEFAULT_EXECUTION_RISK_SETTINGS.stopLoss,
  target: executionRisk?.target ?? DEFAULT_EXECUTION_RISK_SETTINGS.target,
  maximumDailyLoss: executionRisk?.maximumDailyLoss ?? DEFAULT_EXECUTION_RISK_SETTINGS.maximumDailyLoss,
  maximumTradesPerDay: executionRisk?.maximumTradesPerDay ?? DEFAULT_EXECUTION_RISK_SETTINGS.maximumTradesPerDay,
  allowBuy: executionRisk?.allowBuy ?? DEFAULT_EXECUTION_RISK_SETTINGS.allowBuy,
  allowSell: executionRisk?.allowSell ?? DEFAULT_EXECUTION_RISK_SETTINGS.allowSell,
  oneTradePerSymbolPerDay: executionRisk?.oneTradePerSymbolPerDay ?? DEFAULT_EXECUTION_RISK_SETTINGS.oneTradePerSymbolPerDay,
});
const getAllowedSides = (executionRisk) => [
  executionRisk?.allowBuy ? "BUY" : null,
  executionRisk?.allowSell ? "SELL" : null,
].filter(Boolean).join(" / ") || "—";
const getExecutionRiskUserId = (executionRisk) =>
  executionRisk?.userId ??
  executionRisk?.user_id ??
  executionRisk?.user?.userId ??
  executionRisk?.user?.id ??
  null;
const belongsToExecutionRiskUser = (executionRisk, userId) => {
  const recordUserId = getExecutionRiskUserId(executionRisk);
  return recordUserId == null || String(recordUserId) === String(userId);
};

const PodSectorStrategy = () => {
  const { user } = useAuth();
  const { sectorialIndicesData, stockPreOpenData, stockAnalysisData } =
    useStockApiWebsocket(
      environment.stockApiDayRangeDataWSUrl,
      ["sectorialIndicesData", "stockPreOpenData", "stockAnalysisData"]
    );
  const [sectorDepth, setSectorDepth] = useState(3);
  const [signal, setSignal] = useState("ALL");
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [conditionsOpen, setConditionsOpen] = useState(false);
  const [spreadSortDirection, setSpreadSortDirection] = useState(null);
  const [spreadPercentSortDirection, setSpreadPercentSortDirection] = useState(null);
  const [pdcOpenSortDirection, setPdcOpenSortDirection] = useState(null);
  const [fixedControlsHeight, setFixedControlsHeight] = useState(0);
  const [headerPortalTarget, setHeaderPortalTarget] = useState(null);
  const [selectedStockSymbols, setSelectedStockSymbols] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [selectedBrokerId, setSelectedBrokerId] = useState("");
  const [brokerLoading, setBrokerLoading] = useState(true);
  const [brokerLoadError, setBrokerLoadError] = useState(false);
  const [strategyName, setStrategyName] = useState("");
  const [selectedStocksSaved, setSelectedStocksSaved] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState("POD_STRATEGY");
  const [executionRiskSaved, setExecutionRiskSaved] = useState(false);
  const [executionRiskId, setExecutionRiskId] = useState(null);
  const [executionRiskRecords, setExecutionRiskRecords] = useState([]);
  const [executionRiskLoading, setExecutionRiskLoading] = useState(false);
  const [executionRiskSaving, setExecutionRiskSaving] = useState(false);
  const [executionRiskDeletingId, setExecutionRiskDeletingId] = useState(null);
  const [executionRiskError, setExecutionRiskError] = useState("");
  const [executionRiskMessage, setExecutionRiskMessage] = useState("");
  const [executionSettings, setExecutionSettings] = useState({
    ...DEFAULT_EXECUTION_RISK_SETTINGS,
  });
  const conditionsRef = useRef(null);
  const fixedControlsRef = useRef(null);

  useEffect(() => {
    const closeConditions = (event) => {
      if (conditionsRef.current && !conditionsRef.current.contains(event.target)) setConditionsOpen(false);
    };
    document.addEventListener("mousedown", closeConditions);
    return () => document.removeEventListener("mousedown", closeConditions);
  }, []);

  useEffect(() => {
    console.log("POD original data:", stockPreOpenData);
  }, [stockPreOpenData]);

  useEffect(() => {
    let active = true;

    if (!user?.userId) {
      setBrokerLoading(false);
      return undefined;
    }

    setBrokerLoading(true);
    setBrokerLoadError(false);
    getBrokerAccounts(user.userId)
      .then((data) => {
        if (!active) return;
        const connectedBrokers = (Array.isArray(data) ? data : [])
          .filter((broker) => isBrokerConnected(broker?.status));
        setBrokers(connectedBrokers);
        setSelectedBrokerId((currentBrokerId) => {
          const brokerStillAvailable = connectedBrokers.some(
            (broker) => String(getBrokerId(broker)) === currentBrokerId
          );
          return brokerStillAvailable
            ? currentBrokerId
            : "";
        });
      })
      .catch(() => {
        if (!active) return;
        setBrokers([]);
        setSelectedBrokerId("");
        setBrokerLoadError(true);
      })
      .finally(() => {
        if (active) setBrokerLoading(false);
      });

    return () => {
      active = false;
    };
  }, [user?.userId]);

  useEffect(() => {
    if (activeSettingsTab !== "EXECUTION_RISK") return undefined;

    let active = true;

    setExecutionRiskSaved(false);
    setExecutionRiskId(null);
    setExecutionRiskRecords([]);
    setExecutionRiskError("");
    setExecutionRiskMessage("");

    if (!user?.userId) {
      setExecutionSettings({ ...DEFAULT_EXECUTION_RISK_SETTINGS });
      setExecutionRiskLoading(false);
      return undefined;
    }

    const loadExecutionRisk = async () => {
      setExecutionRiskLoading(true);

      try {
        const allExecutionRisk = await getAllExecutionRisk();
        const userExecutionRisk = allExecutionRisk.filter(
          (risk) => belongsToExecutionRiskUser(risk, user.userId)
        );
        const matchingExecutionRisk = userExecutionRisk[0];

        if (!active) return;
        setExecutionRiskRecords(userExecutionRisk);
        if (!matchingExecutionRisk) {
          setExecutionSettings({ ...DEFAULT_EXECUTION_RISK_SETTINGS });
          return;
        }

        const id = getExecutionRiskId(matchingExecutionRisk);
        const executionRiskResponse = id
          ? await getExecutionRiskById(id, user.userId)
          : matchingExecutionRisk;
        const executionRisk = executionRiskResponse && typeof executionRiskResponse === "object"
          ? executionRiskResponse
          : matchingExecutionRisk;

        if (!active) return;
        setExecutionRiskId(id ?? getExecutionRiskId(executionRisk));
        setExecutionSettings(executionRiskToSettings(executionRisk || matchingExecutionRisk));
      } catch (error) {
        if (!active) return;
        setExecutionSettings({ ...DEFAULT_EXECUTION_RISK_SETTINGS });
        setExecutionRiskError(error?.response?.data?.message || error?.message || "Unable to load execution risk");
      } finally {
        if (active) setExecutionRiskLoading(false);
      }
    };

    loadExecutionRisk();

    return () => {
      active = false;
    };
  }, [activeSettingsTab, user?.userId]);

  useEffect(() => {
    setHeaderPortalTarget(document.getElementById("strategy-header-slot"));
  }, []);

  useEffect(() => {
    const fixedControls = fixedControlsRef.current;
    if (!fixedControls) return undefined;

    const updateFixedControlsHeight = () => {
      setFixedControlsHeight(Math.ceil(fixedControls.getBoundingClientRect().height));
    };
    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(updateFixedControlsHeight)
      : null;

    updateFixedControlsHeight();
    resizeObserver?.observe(fixedControls);
    window.addEventListener("resize", updateFixedControlsHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateFixedControlsHeight);
    };
  }, []);

  const toggleCondition = (condition) => {
    setSelectedStocksSaved(false);
    setSelectedConditions((current) => {
      if (current.includes(condition)) return current.filter((item) => item !== condition);

      const mutuallyExclusiveCondition =
        condition === "SECTOR" ? "WSECTOR" :
          condition === "WSECTOR" ? "SECTOR" :
            null;
      const nextConditions = mutuallyExclusiveCondition
        ? current.filter((item) => item !== mutuallyExclusiveCondition)
        : current;

      return [...nextConditions, condition];
    });
  };

  const toggleSelectedStock = (symbol) => {
    setSelectedStocksSaved(false);
    setSelectedStockSymbols((currentSymbols) =>
      currentSymbols.includes(symbol)
        ? currentSymbols.filter((currentSymbol) => currentSymbol !== symbol)
        : [...currentSymbols, symbol]
    );
  };

  const updateExecutionSetting = (field, value) => {
    setExecutionRiskSaved(false);
    setExecutionRiskError("");
    setExecutionRiskMessage("");
    setExecutionSettings((current) => ({ ...current, [field]: value }));
  };

  const saveSelectedStocks = () => {
    const normalizedStrategyName = strategyName.trim();
    const selectedBroker = brokers.find(
      (broker) => String(getBrokerId(broker)) === selectedBrokerId
    );
    if (
      !normalizedStrategyName ||
      !selectedBroker ||
      selectedConditions.length === 0 ||
      selectedStockSymbols.length === 0
    ) return;

    window.localStorage.setItem(
      "podSectorSelectedStocks",
      JSON.stringify(selectedStockSymbols)
    );
    let savedStrategies = [];

    try {
      const storedStrategies = JSON.parse(
        window.localStorage.getItem("podSectorSavedStrategies") || "[]"
      );
      savedStrategies = Array.isArray(storedStrategies) ? storedStrategies : [];
    } catch {
      savedStrategies = [];
    }

    const savedStrategy = {
      name: normalizedStrategyName,
      brokerAccountId: getBrokerId(selectedBroker),
      strategyType: "POD_SECTOR",
      strategyVersion: "POD_SECTOR_V1",
      status: "DRAFT",
      exchange: "NSE",
      sectorDepth,
      psFilter: signal,
      conditionLogic: "AND",
      conditions: [...selectedConditions],
      stocks: [...selectedStockSymbols],
      savedAt: new Date().toISOString(),
    };
    const existingStrategyIndex = savedStrategies.findIndex(
      (strategy) => strategy?.name === normalizedStrategyName
    );

    if (existingStrategyIndex >= 0) {
      savedStrategies[existingStrategyIndex] = savedStrategy;
    } else {
      savedStrategies.push(savedStrategy);
    }

    window.localStorage.setItem(
      "podSectorSavedStrategies",
      JSON.stringify(savedStrategies)
    );
    setSelectedStocksSaved(true);
  };

  const saveExecutionRiskSettings = async () => {
    const quantity = toNumber(executionSettings.quantity);
    const maximumTradesPerDay = toNumber(executionSettings.maximumTradesPerDay);
    if (
      !user?.userId ||
      quantity == null ||
      quantity < 1 ||
      maximumTradesPerDay == null ||
      maximumTradesPerDay < 1 ||
      !isValidTime24(executionSettings.startTime) ||
      !isValidTime24(executionSettings.endTime) ||
      !isValidTime24(executionSettings.squareOffTime) ||
      (!executionSettings.allowBuy && !executionSettings.allowSell)
    ) return;

    const payload = buildExecutionRiskPayload(user.userId, executionSettings);

    setExecutionRiskSaving(true);
    setExecutionRiskError("");
    setExecutionRiskMessage("");

    try {
      const savedExecutionRisk = executionRiskId
        ? await updateExecutionRisk(executionRiskId, user.userId, payload)
        : await createExecutionRisk(payload);
      let savedId = executionRiskId ?? getExecutionRiskId(savedExecutionRisk);
      const savedRecord = savedExecutionRisk && typeof savedExecutionRisk === "object"
        ? { ...payload, ...savedExecutionRisk }
        : payload;

      const allExecutionRisk = await getAllExecutionRisk();
      const userExecutionRisk = allExecutionRisk.filter(
        (risk) => belongsToExecutionRiskUser(risk, user.userId)
      );

      if (!savedId) {
        savedId = getExecutionRiskId(
          userExecutionRisk[0]
        );
      }

      setExecutionRiskRecords(userExecutionRisk.length ? userExecutionRisk : [
        { ...savedRecord, ...(savedId ? { id: savedId } : {}) },
      ]);
      setExecutionRiskId(savedId);
      setExecutionSettings(executionRiskToSettings(savedRecord));
      setExecutionRiskSaved(true);
      setExecutionRiskMessage(
        executionRiskId
          ? "Execution risk updated successfully."
          : "Execution risk saved successfully."
      );
      console.log("POD execution risk payload:", payload);
    } catch (error) {
      setExecutionRiskSaved(false);
      setExecutionRiskError(error?.response?.data?.message || error?.message || "Unable to save execution risk");
    } finally {
      setExecutionRiskSaving(false);
    }
  };

  const editExecutionRiskSettings = async (executionRisk) => {
    const id = getExecutionRiskId(executionRisk);
    if (!id || !user?.userId || executionRiskLoading) return;

    setExecutionRiskLoading(true);
    setExecutionRiskSaved(false);
    setExecutionRiskError("");
    setExecutionRiskMessage("");

    try {
      const executionRiskDetails = await getExecutionRiskById(id, user.userId);
      const record = executionRiskDetails && typeof executionRiskDetails === "object"
        ? executionRiskDetails
        : executionRisk;
      setExecutionRiskId(id);
      setExecutionSettings(executionRiskToSettings(record));
      setExecutionRiskMessage("Execution risk loaded for editing.");
    } catch (error) {
      setExecutionRiskError(error?.response?.data?.message || error?.message || "Unable to load execution risk");
    } finally {
      setExecutionRiskLoading(false);
    }
  };

  const removeExecutionRiskSettings = async (id) => {
    if (!id || !user?.userId || executionRiskDeletingId) return;
    if (!window.confirm("Delete the saved execution risk settings?")) return;

    setExecutionRiskDeletingId(id);
    setExecutionRiskError("");
    setExecutionRiskMessage("");

    try {
      await deleteExecutionRisk(id, user.userId);
      const allExecutionRisk = await getAllExecutionRisk();
      setExecutionRiskRecords(allExecutionRisk.filter(
        (executionRisk) => belongsToExecutionRiskUser(executionRisk, user.userId)
      ));
      if (String(executionRiskId) === String(id)) {
        setExecutionRiskId(null);
        setExecutionSettings({ ...DEFAULT_EXECUTION_RISK_SETTINGS });
        setExecutionRiskSaved(false);
      }
      setExecutionRiskMessage("Execution risk deleted successfully.");
    } catch (error) {
      setExecutionRiskError(error?.response?.data?.message || error?.message || "Unable to delete execution risk");
    } finally {
      setExecutionRiskDeletingId(null);
    }
  };

  const toggleSpreadSort = () => {
    setSpreadPercentSortDirection(null);
    setPdcOpenSortDirection(null);
    setSpreadSortDirection((current) =>
      current === "asc" ? "desc" : current === "desc" ? null : "asc"
    );
  };

  const toggleSpreadPercentSort = () => {
    setSpreadSortDirection(null);
    setPdcOpenSortDirection(null);
    setSpreadPercentSortDirection((current) =>
      current === "asc" ? "desc" : current === "desc" ? null : "asc"
    );
  };

  const togglePdcOpenSort = () => {
    setSpreadSortDirection(null);
    setSpreadPercentSortDirection(null);
    setPdcOpenSortDirection((current) =>
      current === "asc" ? "desc" : current === "desc" ? null : "asc"
    );
  };

  const rankedSectors = useMemo(() => (Array.isArray(sectorialIndicesData) ? sectorialIndicesData : [])
    .map((sector) => ({ sector, name: getSectorName(sector), percent: toNumber(sector?.pChange) }))
    .filter((item) => item.name && item.name !== "MEDIA" && item.percent != null)
    .sort((a, b) => a.percent - b.percent), [sectorialIndicesData]);
  const lowestSectors = useMemo(() => rankedSectors.slice(0, sectorDepth), [rankedSectors, sectorDepth]);
  const highestSectors = useMemo(() => rankedSectors.slice(-sectorDepth).reverse(), [rankedSectors, sectorDepth]);

  const stockSectorMap = useMemo(() => {
    const map = new Map();
    [...highestSectors, ...lowestSectors, ...rankedSectors].forEach(({ sector, name, percent }) =>
      (sector?.sectorialIndicesStockModelList || []).forEach((stock) => {
        const symbol = normalizeSymbol(stock?.symbol ?? stock?.stockSymbol ?? stock?.tradingSymbol);
        if (symbol && !map.has(symbol)) map.set(symbol, { name, percent });
      }));
    return map;
  }, [highestSectors, lowestSectors, rankedSectors]);

  const analysisMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(stockAnalysisData) ? stockAnalysisData : []).forEach((stock) => {
      const symbol = normalizeSymbol(stock?.symbol ?? stock?.stockSymbol ?? stock?.tradingSymbol);
      if (symbol) map.set(symbol, stock);
    });
    return map;
  }, [stockAnalysisData]);

  const candidates = useMemo(() => {
    const lowestNames = new Map(lowestSectors.map((item, index) => [item.name, index + 1]));
    const highestNames = new Map(highestSectors.map((item, index) => [item.name, index + 1]));
    const allPodStocks = getRows(stockPreOpenData).map((entry) => {
      const metadata = entry?.metadata ?? entry ?? {};
      const detail = entry?.detail ?? {};
      const preOpenMarket = detail?.preOpenMarket ?? entry?.preOpenMarket ?? {};
      const symbol = normalizeSymbol(metadata?.symbol ?? entry?.symbol);
      const analysis = analysisMap.get(symbol) || {};
      const sector = stockSectorMap.get(symbol);
      const previousClose = toNumber(detail?.previousClose ?? metadata?.previousClose ?? entry?.previousClose ?? analysis?.previousClose);
      const ltp = toNumber(analysis?.lp ?? analysis?.ltp ?? metadata?.ltp ?? entry?.ltp);
      const open = toNumber(
        preOpenMarket?.finalPrice ??
        metadata?.finalPrice ??
        detail?.finalPrice ??
        entry?.finalPrice
      );
      const support = toNumber(entry?.support ?? metadata?.support ?? detail?.support ?? analysis?.support);
      const resistance = toNumber(entry?.resistance ?? metadata?.resistance ?? detail?.resistance ?? analysis?.resistance);
      const firstFiveMinuteHigh = toNumber(
        analysis?.firstFiveMinuteHigh ??
        analysis?.first5MinuteHigh ??
        entry?.firstFiveMinuteHigh ??
        metadata?.firstFiveMinuteHigh ??
        detail?.firstFiveMinuteHigh ??
        entry?.firstFiveHigh
      );
      const firstFiveMinuteLow = toNumber(
        analysis?.firstFiveMinuteLow ??
        analysis?.first5MinuteLow ??
        entry?.firstFiveMinuteLow ??
        metadata?.firstFiveMinuteLow ??
        detail?.firstFiveMinuteLow ??
        entry?.firstFiveLow
      );
      const ltpFiveMinuteDirection =
        ltp != null && firstFiveMinuteHigh != null && ltp >= firstFiveMinuteHigh
          ? "up"
          : ltp != null && firstFiveMinuteLow != null && ltp <= firstFiveMinuteLow
            ? "down"
            : null;
      const lowestRank = lowestNames.get(sector?.name);
      const highestRank = highestNames.get(sector?.name);
      const hasStrategyLevels =
        open != null && ltp != null && support != null && resistance != null;
      const podStrategySignal =
        hasStrategyLevels &&
        open >= support && open >= resistance &&
        ltp >= support && ltp >= resistance
          ? "BUY"
          : hasStrategyLevels &&
            open <= support && open <= resistance &&
            ltp <= support && ltp <= resistance
            ? "SELL"
            : "";
      return {
        symbol, previousClose, ltp, open, support, resistance,
        firstFiveMinuteHigh, firstFiveMinuteLow, ltpFiveMinuteDirection, podStrategySignal,
        sectorName: sector?.name, sectorPercent: sector?.percent,
        sectorRank: lowestRank
          ? `${ordinal(lowestRank)} L`
          : highestRank
            ? `${ordinal(highestRank)} H`
            : "",
        oiPercent: toNumber(entry?.avgInOI),
        livePercent: toNumber(analysis?.chp ?? entry?.chp),
      };
    });

    const rankedSectorStocks = allPodStocks
      .filter((stock) => Boolean(stock.sectorRank))
      .sort((first, second) => {
        const firstGroup = first.sectorRank.endsWith("H") ? 0 : 1;
        const secondGroup = second.sectorRank.endsWith("H") ? 0 : 1;
        const firstRank = Number.parseInt(first.sectorRank, 10) || 99;
        const secondRank = Number.parseInt(second.sectorRank, 10) || 99;

        return firstGroup - secondGroup ||
          firstRank - secondRank ||
          String(first.sectorName).localeCompare(String(second.sectorName)) ||
          first.symbol.localeCompare(second.symbol);
      });
    const sortBySpread = (stocks) => {
      const activeSortDirection =
        pdcOpenSortDirection || spreadPercentSortDirection || spreadSortDirection;
      if (!activeSortDirection) return stocks;

      return stocks
        .map((stock, index) => ({ stock, index }))
        .sort((first, second) => {
          const firstSpread = pdcOpenSortDirection
            ? getPreviousCloseOpenDifference(first.stock.previousClose, first.stock.open)
            : spreadPercentSortDirection
              ? getResistanceSupportPercentage(first.stock.support, first.stock.resistance)
              : getSupportResistanceDifference(first.stock.support, first.stock.resistance);
          const secondSpread = pdcOpenSortDirection
            ? getPreviousCloseOpenDifference(second.stock.previousClose, second.stock.open)
            : spreadPercentSortDirection
              ? getResistanceSupportPercentage(second.stock.support, second.stock.resistance)
              : getSupportResistanceDifference(second.stock.support, second.stock.resistance);

          if (firstSpread == null && secondSpread == null) return first.index - second.index;
          if (firstSpread == null) return 1;
          if (secondSpread == null) return -1;
          if (firstSpread === secondSpread) return first.index - second.index;

          return activeSortDirection === "asc"
            ? firstSpread - secondSpread
            : secondSpread - firstSpread;
        })
        .map(({ stock }) => stock);
    };

    // Initially show the complete POD feed. The SECTOR condition narrows the
    // feed to stocks from the selected highest and lowest sector ranks.
    if (signal === "ALL" && selectedConditions.length === 0) {
      return sortBySpread(allPodStocks);
    }

    const conditionSource = selectedConditions.includes("SECTOR")
      ? rankedSectorStocks
      : allPodStocks;

    return sortBySpread(conditionSource.filter((stock) => signal === "ALL" || stock.podStrategySignal === signal)
      .filter((stock) => selectedConditions.every((condition) => {
        if (condition === "SECTOR") return Boolean(stock.sectorName);
        if (condition === "WSECTOR") return !stock.sectorName;
        if (condition === "LTPGSR") return stock.ltp != null && stock.support != null && stock.resistance != null && stock.ltp >= stock.support && stock.ltp >= stock.resistance;
        if (condition === "LTPLSR") return stock.ltp != null && stock.support != null && stock.resistance != null && stock.ltp <= stock.support && stock.ltp <= stock.resistance;
        if (condition === "OGSR") return stock.open != null && stock.support != null && stock.resistance != null && stock.open >= stock.support && stock.open >= stock.resistance;
        if (condition === "OLSR") return stock.open != null && stock.support != null && stock.resistance != null && stock.open <= stock.support && stock.open <= stock.resistance;
        if (condition === "LTPG500") return stock.ltp != null && stock.ltp >= 500;
        return true;
      })));
  }, [analysisMap, highestSectors, lowestSectors, pdcOpenSortDirection, selectedConditions, signal, spreadPercentSortDirection, spreadSortDirection, stockPreOpenData, stockSectorMap]);

  const canSaveStrategy = Boolean(
    selectedBrokerId &&
    strategyName.trim() &&
    selectedConditions.length > 0 &&
    selectedStockSymbols.length > 0
  );
  const canSaveExecutionRisk = Boolean(
    user?.userId &&
    (toNumber(executionSettings.quantity) ?? 0) >= 1 &&
    (toNumber(executionSettings.maximumTradesPerDay) ?? 0) >= 1 &&
    isValidTime24(executionSettings.startTime) &&
    isValidTime24(executionSettings.endTime) &&
    isValidTime24(executionSettings.squareOffTime) &&
    (executionSettings.allowBuy || executionSettings.allowSell)
  );

  return <main
    className="algo-page pod-sector-strategy-page"
    style={{ "--pod-fixed-controls-height": `${fixedControlsHeight}px` }}
  >
    {headerPortalTarget && createPortal(
      <div className="pod-header-sector-summary">
        <div className="pod-header-sector-highest">
          <span>Highest Sectors</span>
          <strong>{highestSectors.map(formatSector).join(", ") || "Waiting..."}</strong>
        </div>
        <div className="pod-header-sector-lowest">
          <span>Lowest Sectors</span>
          <strong>{lowestSectors.map(formatSector).join(", ") || "Waiting..."}</strong>
        </div>
        <div className="pod-header-matching-stocks">
          <span>Matching Stocks</span>
          <strong>{candidates.length}</strong>
        </div>
      </div>,
      headerPortalTarget
    )}
    <div className="pod-settings-tab-container">
      <div className="pod-settings-tabs" role="tablist" aria-label="POD strategy settings">
        <button
          type="button"
          role="tab"
          aria-selected={activeSettingsTab === "POD_STRATEGY"}
          className={`pod-settings-tablinks ${activeSettingsTab === "POD_STRATEGY" ? "active" : ""}`}
          onClick={() => setActiveSettingsTab("POD_STRATEGY")}
        >
          POD STRATEGY ({candidates.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeSettingsTab === "EXECUTION_RISK"}
          className={`pod-settings-tablinks ${activeSettingsTab === "EXECUTION_RISK" ? "active" : ""}`}
          onClick={() => setActiveSettingsTab("EXECUTION_RISK")}
        >
          EXECUTION &amp; RISK
        </button>
      </div>
    </div>
    <div className="pod-strategy-fixed-controls" ref={fixedControlsRef}>
      {activeSettingsTab === "POD_STRATEGY" && (
      <div className="pod-strategy-setup-row">
        <section className="algo-content-card pod-filter-card">
      <label className="algo-field"><span>Sector Ranks</span><select value={sectorDepth} onChange={(e) => { setSectorDepth(Number(e.target.value)); setSelectedStocksSaved(false); }}><option value="1">1st only</option><option value="2">1st and 2nd</option><option value="3">1st, 2nd and 3rd</option></select></label>
      <label className="algo-field"><span>PS</span><select value={signal} onChange={(e) => { setSignal(e.target.value); setSelectedStocksSaved(false); }}><option value="ALL">All Signals</option><option value="BUY">BUY</option><option value="SELL">SELL</option></select></label>
      <div className="algo-field pod-multi-select" ref={conditionsRef}>
        <span>Strategy Conditions</span>
        <button type="button" className="pod-multi-select-trigger" onClick={() => setConditionsOpen((open) => !open)} aria-expanded={conditionsOpen}>
          <span>{selectedConditions.length ? `${selectedConditions.length} selected` : "Select conditions"}</span>
          <i className={`fa fa-chevron-${conditionsOpen ? "up" : "down"}`}></i>
        </button>
        {conditionsOpen && <div className="pod-multi-select-menu">
          {STRATEGY_OPTIONS.map((option) => <label key={option.value}>
            <input type="checkbox" checked={selectedConditions.includes(option.value)} onChange={() => toggleCondition(option.value)} />
            <span>{option.label}</span>
          </label>)}
          <button type="button" className="pod-clear-conditions" onClick={() => { setSelectedConditions([]); setSelectedStocksSaved(false); }}>CLEAR</button>
        </div>}
      </div>
        </section>
        <section className="algo-content-card pod-strategy-save-card">
          <label className="algo-field">
            <span>Broker</span>
            <select
              value={selectedBrokerId}
              onChange={(event) => {
                setSelectedBrokerId(event.target.value);
                setSelectedStocksSaved(false);
              }}
              disabled={brokerLoading || brokers.length === 0}
            >
              {brokerLoading
                ? <option value="">Loading brokers...</option>
                : brokerLoadError
                  ? <option value="">Unable to load brokers</option>
                  : brokers.length === 0
                    ? <option value="">No connected brokers</option>
                    : <>
                      <option value="">Select Broker</option>
                      {brokers.map((broker) => (
                        <option value={String(getBrokerId(broker))} key={getBrokerId(broker)}>
                          {broker.accountName} ({broker.brokerType})
                        </option>
                      ))}
                    </>}
            </select>
          </label>
          <label className="algo-field">
            <span>Strategy Name</span>
            <input
              type="text"
              value={strategyName}
              placeholder="Enter strategy name"
              onChange={(event) => {
                setStrategyName(event.target.value);
                setSelectedStocksSaved(false);
              }}
            />
          </label>
          <div className="algo-field pod-selected-stocks-panel">
            <span>Selected Stocks ({selectedStockSymbols.length})</span>
            <div className="pod-selected-stock-symbols">
              {selectedStockSymbols.length
                ? selectedStockSymbols.map((symbol) => (
                  <button
                    type="button"
                    className="pod-selected-stock-chip"
                    key={symbol}
                    onClick={() => toggleSelectedStock(symbol)}
                    aria-label={`Remove ${symbol} from selected stocks`}
                    title={`Remove ${symbol}`}
                  >
                    <span>{symbol}</span>
                    <span aria-hidden="true">×</span>
                  </button>
                ))
                : <span className="pod-no-selected-stocks">No stocks selected</span>}
            </div>
          </div>
          <button
            type="button"
            className="pod-save-selected-stocks"
            onClick={saveSelectedStocks}
            disabled={!canSaveStrategy}
          >
            {selectedStocksSaved ? "SAVED" : "SAVE"}
          </button>
        </section>
      </div>
      )}
      {activeSettingsTab === "EXECUTION_RISK" && (
      <section className="algo-content-card pod-trade-config-card">
        <div className="pod-trade-config-heading">
          <div className="pod-trade-config-copy">
            <strong>Execution &amp; Risk ({executionRiskRecords.length})</strong>
            <span>These settings are saved separately for the signed-in user.</span>
            {executionRiskLoading && <span className="pod-execution-risk-status">Loading...</span>}
            {!executionRiskLoading && executionRiskError && <span className="pod-execution-risk-status error">{executionRiskError}</span>}
            {!executionRiskLoading && !executionRiskError && executionRiskMessage && <span className="pod-execution-risk-status success">{executionRiskMessage}</span>}
          </div>
          <div className="pod-execution-risk-actions">
            <button
              type="button"
              className="pod-save-execution-risk"
              onClick={saveExecutionRiskSettings}
              disabled={!canSaveExecutionRisk || executionRiskLoading || executionRiskSaving || Boolean(executionRiskDeletingId)}
            >
              {executionRiskSaving
                ? "SAVING..."
                : executionRiskSaved
                  ? executionRiskId ? "UPDATED" : "SAVED"
                  : executionRiskId ? "UPDATE RISK" : "SAVE RISK"}
            </button>
          </div>
        </div>
        <div className="pod-trade-config-grid">
          <label className="algo-field"><span>Trading Mode</span><select value={executionSettings.tradingMode} onChange={(e) => updateExecutionSetting("tradingMode", e.target.value)}><option value="PAPER">PAPER</option><option value="LIVE">LIVE</option></select></label>
          <label className="algo-field"><span>Product</span><select value={executionSettings.productType} onChange={(e) => updateExecutionSetting("productType", e.target.value)}><option value="INTRADAY">INTRADAY</option><option value="DELIVERY">DELIVERY</option></select></label>
          <label className="algo-field"><span>Order Type</span><select value={executionSettings.orderType} onChange={(e) => updateExecutionSetting("orderType", e.target.value)}><option value="MARKET">MARKET</option><option value="LIMIT">LIMIT</option></select></label>
          <label className="algo-field"><span>Quantity</span><input type="number" min="1" step="1" value={executionSettings.quantity} onChange={(e) => updateExecutionSetting("quantity", e.target.value)} /></label>
          <label className="algo-field"><span>Start Time</span><input type="text" inputMode="numeric" maxLength="5" pattern="(?:[01]\d|2[0-3]):[0-5]\d" title="Enter time in 24-hour HH:mm format" placeholder="HH:mm" value={executionSettings.startTime} onChange={(e) => updateExecutionSetting("startTime", e.target.value)} /></label>
          <label className="algo-field"><span>End Time</span><input type="text" inputMode="numeric" maxLength="5" pattern="(?:[01]\d|2[0-3]):[0-5]\d" title="Enter time in 24-hour HH:mm format" placeholder="HH:mm" value={executionSettings.endTime} onChange={(e) => updateExecutionSetting("endTime", e.target.value)} /></label>
          <label className="algo-field"><span>Square Off</span><input type="text" inputMode="numeric" maxLength="5" pattern="(?:[01]\d|2[0-3]):[0-5]\d" title="Enter time in 24-hour HH:mm format" placeholder="HH:mm" value={executionSettings.squareOffTime} onChange={(e) => updateExecutionSetting("squareOffTime", e.target.value)} /></label>
          <label className="algo-field"><span>Stop Loss (₹)</span><input type="number" min="0" step="0.01" placeholder="0 = disabled" value={executionSettings.stopLoss} onChange={(e) => updateExecutionSetting("stopLoss", e.target.value)} /></label>
          <label className="algo-field"><span>Target (₹)</span><input type="number" min="0" step="0.01" placeholder="0 = disabled" value={executionSettings.target} onChange={(e) => updateExecutionSetting("target", e.target.value)} /></label>
          <label className="algo-field"><span>Max Daily Loss (₹)</span><input type="number" min="0" step="0.01" placeholder="0 = disabled" value={executionSettings.maximumDailyLoss} onChange={(e) => updateExecutionSetting("maximumDailyLoss", e.target.value)} /></label>
          <label className="algo-field"><span>Max Trades</span><input type="number" min="1" step="1" value={executionSettings.maximumTradesPerDay} onChange={(e) => updateExecutionSetting("maximumTradesPerDay", e.target.value)} /></label>
          <div className="algo-field pod-trade-check-field">
            <span>Allowed Side</span>
            <div className="pod-trade-checks">
              <label><input type="checkbox" checked={executionSettings.allowBuy} onChange={(e) => updateExecutionSetting("allowBuy", e.target.checked)} /> BUY</label>
              <label><input type="checkbox" checked={executionSettings.allowSell} onChange={(e) => updateExecutionSetting("allowSell", e.target.checked)} /> SELL</label>
            </div>
          </div>
          <div className="algo-field pod-trade-check-field">
            <span>Duplicate Protection</span>
            <div className="pod-trade-checks">
              <label><input type="checkbox" checked={executionSettings.oneTradePerSymbolPerDay} onChange={(e) => updateExecutionSetting("oneTradePerSymbolPerDay", e.target.checked)} /> One trade/symbol/day</label>
            </div>
          </div>
        </div>
      </section>
      )}
      {activeSettingsTab === "POD_STRATEGY" && (
      <section className="algo-content-card pod-selected-conditions-card">
        <div className="pod-rule-note">
        <strong>Selected Conditions:</strong>
        {selectedConditions.length
          ? selectedConditions.map((condition, index) => <React.Fragment key={condition}>
            {index > 0 && <span></span>}
            <b>{condition}:</b> {CONDITION_DESCRIPTIONS[condition]}
          </React.Fragment>)
          : "No conditions selected — showing all ranked-sector stocks"}
        </div>
      </section>
      )}
    </div>
    {activeSettingsTab === "POD_STRATEGY" && (
    <section className="algo-content-card pod-strategy-table-card">
      <div className="algo-table-wrap pod-strategy-table-wrap"><table className="algo-table pod-strategy-table"><thead><tr><th>Symbol</th><th>Sector</th><th>Rank</th><th>PDC</th><th>SUP</th><th>RES</th><th><button type="button" className={`pod-spread-sort ${spreadSortDirection ? "active" : ""}`} onClick={toggleSpreadSort} title="Click: ascending, descending, normal">R-S Diff <span>{spreadSortDirection === "asc" ? "▲" : spreadSortDirection === "desc" ? "▼" : "↕"}</span></button></th><th><button type="button" className={`pod-spread-sort ${spreadPercentSortDirection ? "active" : ""}`} onClick={toggleSpreadPercentSort} title="Click: ascending, descending, normal">R-S % <span>{spreadPercentSortDirection === "asc" ? "▲" : spreadPercentSortDirection === "desc" ? "▼" : "↕"}</span></button></th><th>Open</th><th><button type="button" className={`pod-spread-sort ${pdcOpenSortDirection ? "active" : ""}`} onClick={togglePdcOpenSort} title="Click: ascending, descending, normal">P-O Diff <span>{pdcOpenSortDirection === "asc" ? "▲" : pdcOpenSortDirection === "desc" ? "▼" : "↕"}</span></button></th><th>5MH</th><th>5ML</th><th>LTP</th><th>PS</th><th><label className="pod-select-all-control"><span>ACT</span><input type="checkbox" className="pod-stock-select-checkbox" aria-label="Select all displayed stocks" checked={candidates.length > 0 && candidates.every((stock) => selectedStockSymbols.includes(stock.symbol))} onChange={(event) => { setSelectedStocksSaved(false); setSelectedStockSymbols(event.target.checked ? [...new Set(candidates.map((stock) => stock.symbol))] : []); }} /></label></th><th>OI%</th><th>Live%</th></tr></thead>
        <tbody>{candidates.length ? candidates.map((stock, index) => <tr key={`${stock.symbol}-${index}`}><td><strong>{stock.symbol}</strong></td><td>{stock.sectorName || "—"}</td><td className={stock.sectorRank.endsWith("H") ? "pod-rank-high-text" : stock.sectorRank.endsWith("L") ? "pod-rank-low-text" : ""}>{stock.sectorRank || "—"}</td><td>{formatNumber(stock.previousClose)}</td><td>{formatNumber(stock.support)}</td><td>{formatNumber(stock.resistance)}</td><td>{formatNumber(getSupportResistanceDifference(stock.support, stock.resistance))}</td><td>{getResistanceSupportPercentage(stock.support, stock.resistance) == null ? "—" : `${getResistanceSupportPercentage(stock.support, stock.resistance).toFixed(2)}%`}</td><td>{formatNumber(stock.open)}</td><td className={stock.open != null && stock.previousClose != null ? stock.open >= stock.previousClose ? "pod-pdc-open-positive" : "pod-pdc-open-negative" : ""}>{formatNumber(getPreviousCloseOpenDifference(stock.previousClose, stock.open))}</td><td>{formatNumber(stock.firstFiveMinuteHigh)}</td><td>{formatNumber(stock.firstFiveMinuteLow)}</td><td className={getLtpCellClass(stock)}>{formatNumber(stock.ltp)}{stock.ltpFiveMinuteDirection && <span className={`pod-ltp-direction pod-ltp-direction--${stock.ltpFiveMinuteDirection}`} aria-label={stock.ltpFiveMinuteDirection === "up" ? "LTP is at or above the five-minute high" : "LTP is at or below the five-minute low"}>{stock.ltpFiveMinuteDirection === "up" ? "↑" : "↓"}</span>}</td><td className={stock.podStrategySignal === "BUY" ? "pod-ps-buy" : stock.podStrategySignal === "SELL" ? "pod-ps-sell" : ""}>{stock.podStrategySignal || "—"}</td><td><input type="checkbox" className="pod-stock-select-checkbox" aria-label={`Select ${stock.symbol}`} checked={selectedStockSymbols.includes(stock.symbol)} onChange={() => toggleSelectedStock(stock.symbol)} /></td><td>{formatNumber(stock.oiPercent)}</td><td className={stock.livePercent > 0 ? "pod-live-positive" : stock.livePercent < 0 ? "pod-live-negative" : "pod-live-neutral"}>{formatNumber(stock.livePercent)}</td></tr>) : <tr><td colSpan="17"><div className="algo-empty compact"><i className="fa fa-filter"></i><h2>No matching stocks</h2><p>Waiting for POD data or no stocks match the selected rules.</p></div></td></tr>}</tbody>
      </table></div>
    </section>
    )}
    {activeSettingsTab === "EXECUTION_RISK" && (
    <section className="algo-content-card pod-execution-risk-table-card">
      <div className="algo-table-wrap pod-execution-risk-table-wrap">
        <table className="algo-table pod-execution-risk-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>User ID</th>
              <th>Mode</th>
              <th>Product</th>
              <th>Order</th>
              <th>Qty</th>
              <th>Start</th>
              <th>End</th>
              <th>Square Off</th>
              <th>Stop Loss</th>
              <th>Target</th>
              <th>Max Loss</th>
              <th>Max Trades</th>
              <th>Side</th>
              <th>One/Day</th>
              <th>Created Date</th>
              <th>Created Time</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {executionRiskRecords.length ? executionRiskRecords.map((executionRisk, index) => {
              const id = getExecutionRiskId(executionRisk);
              const isDeleting = id != null && String(executionRiskDeletingId) === String(id);
              const isSelected = id != null && String(executionRiskId) === String(id);
              return (
                <tr key={id ?? `${executionRisk.userId}-${index}`} className={isSelected ? "selected" : ""}>
                  <td>{id ?? "—"}</td>
                  <td>{executionRisk.userId ?? "—"}</td>
                  <td>{executionRisk.tradingMode ?? "—"}</td>
                  <td>{executionRisk.productType ?? "—"}</td>
                  <td>{executionRisk.orderType ?? "—"}</td>
                  <td>{formatNumber(executionRisk.quantity)}</td>
                  <td>{String(executionRisk.startTime ?? "—").slice(0, 5)}</td>
                  <td>{String(executionRisk.endTime ?? "—").slice(0, 5)}</td>
                  <td>{String(executionRisk.squareOffTime ?? "—").slice(0, 5)}</td>
                  <td>{formatNumber(executionRisk.stopLoss)}</td>
                  <td>{formatNumber(executionRisk.target)}</td>
                  <td>{formatNumber(executionRisk.maximumDailyLoss)}</td>
                  <td>{formatNumber(executionRisk.maximumTradesPerDay)}</td>
                  <td>{getAllowedSides(executionRisk)}</td>
                  <td>{executionRisk.oneTradePerSymbolPerDay ? "YES" : "NO"}</td>
                  <td>{executionRisk.createdDate ?? "—"}</td>
                  <td>{executionRisk.createdTime ?? "—"}</td>
                  <td>
                    <div className="pod-execution-risk-row-actions">
                      <button
                        type="button"
                        className="edit"
                        onClick={() => editExecutionRiskSettings(executionRisk)}
                        disabled={!id || executionRiskLoading || executionRiskSaving || Boolean(executionRiskDeletingId)}
                      >
                        EDIT
                      </button>
                      <button
                        type="button"
                        className="delete"
                        onClick={() => removeExecutionRiskSettings(id)}
                        disabled={!id || executionRiskLoading || executionRiskSaving || Boolean(executionRiskDeletingId)}
                      >
                        {isDeleting ? "DELETING..." : "DELETE"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="18">
                  <div className="algo-empty compact">
                    <i className="fa fa-shield"></i>
                    <h2>No execution risk saved</h2>
                    <p>Complete the form above and select Save Risk.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
    )}
  </main>;
};

export default PodSectorStrategy;
